/**
 * Reviews Component
 *
 * This component fetches and displays the latest reviews for a specific doctor.
 * It shows a loading skeleton while the reviews are being fetched,
 * and uses a slider to navigate through the reviews. If there are multiple pages
 * of reviews, a link is provided to view all reviews.
 *
 * Props:
 * - id: The ID of the doctor whose reviews are being fetched.
 * Data: 01-10-2024
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import defaultProfile from "src/assets/images/default_profile_image.png";
import { convertToTimeAgo } from "src/helpers/helperFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faStar } from "@fortawesome/free-solid-svg-icons";
import { IPaginated } from "src/types/paginated";
import ReviewLoader from "src/Components/ReviewLoader";
import { capitalize } from "@mui/material";
import FavStarIcon from "src/Components/GlobalComponents/FavStarIcon";
import { useSelector } from "react-redux";
import { RootState } from "src/Store/store";
import { customer } from "src/lib/constans";

const settings = {
  dots: true,
  infinite: false,
  speed: 800,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  adaptiveHeight: true,
  centerMode: false,
  centerPadding: "70px",
};

interface IReviewItem {
  _id: string;
  comment: string;
  star: number;
  customerFirstName: string;
  customerlastName: string;
  customerProfileImageUrl: string;
  createdAt: string;
  like: string;
  longConsultation: string;
  diagnosis: string;
  doctorConsultation: string;
  updatedAt: string;
}

const Reviews: React.FC<{ id: string; isAdmin?: boolean }> = ({
  id = "",
  isAdmin = false,
}) => {
  const navigate = useNavigate();
  const { userDetails } = useSelector((state: RootState) => state.appReducer);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination
  const [data, setData] = useState<IReviewItem[]>([]);

  const handleNavigate = () => {
    if (isAdmin) {
      navigate(`/doctor/${id}/View?allreview=true`);
    } else if (userDetails?.userRole === customer) {
      navigate(`/doctor/allreviews/${id}/view`);
    } else {
      navigate(`/allreviews/${id}/view`);
    }
  };

  // API_CALL: Fetch latest 5 review from the serve
  const fetchLatestReview = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<{
      documents: IReviewItem[];
      paginated: IPaginated;
    }>({ path: `/review/get-profile/${id}?page=1&limit=5` });
    if (res) {
      let { documents = [], paginated } = res;
      setData(documents);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    fetchLatestReview();
  }, [id]);

  const getStars = (likes: string) => {
    let likeCount = Number(likes);
    return Math.floor(likeCount / 2);
  };

  return (
    <div className="parent-box relative">
      <h2 className="font-size-20px font-Poppins-SemiBold theme-color-green">
        Reviews
      </h2>
      <div>
        {isLoader ? (
          <ReviewLoader />
        ) : (
          <div className="reviews-slider mb-8">
            <Slider {...settings}>
              {data.map((item: IReviewItem, index: number) => (
                <div key={index} className="px-4 py-4">
                  <div className="reviews relative rounded-[15px] bg-[#FFFCF1] shadow-[0px_0px_10px_#00000029] w-full flex gap-2 items-center mt-4 px-4 py-6">
                    <div className="flex items-center justify-center flex-col gap-1 px-5 lg:px-[2vw]">
                      <img
                        src={`${item?.customerProfileImageUrl}`}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (target) {
                            target.src = defaultProfile;
                          }
                        }}
                        alt="profile-img"
                        className="rounded-full w-[65px] h-[65px] object-cover"
                      />
                      <h2 className="font-size-16px font-Poppins-Medium theme-color-green capitalize">
                        {`${item?.customerFirstName || ""} ${
                          item?.customerlastName || ""
                        }`}
                      </h2>
                      <p className="font-size-13px font-Poppins-Regular leading-none">
                        Patient
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                      <div className="flex w-full justify-between">
                        <FavStarIcon rating={getStars(item.like)} />
                        <div className="font-size-13px font-Poppins-Regular text-[#636363]">
                          {convertToTimeAgo(item.updatedAt)}
                        </div>
                      </div>
                      <p className="font-size-15px font-Poppins-Regular text-[#555555]">
                        {item?.comment || ""}
                      </p>
                      <div className="flex gap-2 items-center justify-start flex-wrap">
                        <div className="border border-[#22C55E] bg-[#F0FDF4] rounded-[5px] xl:rounded-[10px] p-2 px-2.5">
                          <p className="font-size-15px font-Poppins-Regular text-[#2E2E2E]">
                            <span className="text-[#555555] ">
                              Waiting Time:
                            </span>{" "}
                            <span className="font-Poppins-Medium">
                              {item.longConsultation}
                            </span>
                          </p>
                        </div>
                        <div className="border border-[#22C55E] rounded-[5px] bg-[#F0FDF4] xl:rounded-[10px] p-2 px-2.5">
                          <p className="font-size-15px font-Poppins-Regular text-[#2E2E2E]">
                            <span className="text-[#555555] ">
                              Consultation Time:
                            </span>{" "}
                            <span className="font-Poppins-Medium">
                              {item.doctorConsultation}
                            </span>
                          </p>
                        </div>
                        <div className="border border-[#22C55E] rounded-[5px] bg-[#F0FDF4] xl:rounded-[10px] p-2 px-2.5">
                          <p className="font-size-15px font-Poppins-Regular text-[#2E2E2E]">
                            <span className="text-[#555555] ">
                              Satisfied with Diagnosis:
                            </span>{" "}
                            <span className="font-Poppins-Medium">
                              {capitalize(item.diagnosis || "no")}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        )}
        {!isLoader && data.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="font-size-16px font-Poppins-Regular text-[#707070]">
              No Reviews
            </p>
          </div>
        ) : null}
      </div>
      {paginated.totalPages > 1 && (
        <div className="pt-3 mt-1 w-full my-4">
          <hr className="border-[#c9c9c9] mb-2" />
          <button
            onClick={handleNavigate}
            className="flex items-center justify-center gap-4 mx-auto"
          >
            <p className="font-size-20px font-Poppins-Regular text-[#707070]">
              Show all reviews
            </p>
            <p className="font-size-18px ">
              <FontAwesomeIcon icon={faArrowRight} className="text-[#707070]" />
            </p>
          </button>
        </div>
      )}
    </div>
  );
};

export default Reviews;
